// src/components/brand/run-history-dialog.tsx
import { useEffect, useMemo, useState } from "react";
import { Clock, Cpu, CheckCircle2, ListChecks, Loader2 } from "lucide-react";
import type { BrandRunSummary } from "@/app/services/brand/brand-access-service";
import { fixActionsService } from "@/app/services/fix-actions/fix-actions-service";
import { CANONICAL_NOT_MEASURED as NOT_MEASURED, Modal } from "../kit";

interface RunHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  brand: string;
  runs: BrandRunSummary[];
  activeRunId?: number | null;
  /** Load the selected run into the dashboard (dialog closes after). */
  onSelect: (runId: number) => void;
}

function formatWhen(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function isImplemented(entry: any): boolean {
  if (!entry) return false;
  if (entry.implemented === true || entry.published === true) return true;
  const s = String(entry.status ?? entry.state ?? "").toLowerCase();
  return ["implemented", "published", "done", "complete", "completed"].includes(s);
}

/**
 * "Implemented" is tracked in the Fix Actions / Action Log system, keyed by
 * the run id (same `brandId` antenna-dashboard uses when ingesting). We fetch
 * per run lazily when the dialog opens.
 */
function useImplementedCounts(open: boolean, runs: BrandRunSummary[]) {
  const [counts, setCounts] = useState<Record<number, { implemented: number; inLog: number }>>({});
  const [loading, setLoading] = useState(false);

  const runIds = useMemo(() => runs.map((r) => r.id).join(","), [runs]);

  useEffect(() => {
    if (!open || !runs.length) return;
    let cancelled = false;
    setLoading(true);

    Promise.allSettled(
      runs.map((run) =>
        fixActionsService
          .getFixActionsPageData({ brandId: String(run.id) })
          .then((pageData: any) => {
            const log: any[] = Array.isArray(pageData?.actionLog) ? pageData.actionLog : [];
            const antenna = log.filter(
              (e) => e?.sourceModule === "antenna" && e?.sourceId,
            );
            return {
              id: run.id,
              implemented: antenna.filter(isImplemented).length,
              inLog: antenna.length,
            };
          }),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next: Record<number, { implemented: number; inLog: number }> = {};
      results.forEach((res) => {
        if (res.status === "fulfilled") {
          next[res.value.id] = {
            implemented: res.value.implemented,
            inLog: res.value.inLog,
          };
        }
      });
      setCounts(next);
      setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, runIds]);

  return { counts, loading };
}

function PriorityChip({
  label, value, color, bg,
}: {
  label: string; value: number; color: string; bg: string;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        borderRadius: 999,
        backgroundColor: bg,
        color,
        fontSize: "var(--text-xs)",
        fontWeight: 700,
      }}
    >
      <span style={{ opacity: 0.8, fontWeight: 600 }}>{label}</span>
      {value}
    </span>
  );
}

export function RunHistoryDialog({
  open,
  onClose,
  brand,
  runs,
  activeRunId,
  onSelect,
}: RunHistoryDialogProps) {
  const { counts, loading } = useImplementedCounts(open, runs);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="form"
      title={`Run history, ${brand}`}
      description={`${runs.length} run${runs.length === 1 ? "" : "s"}. Select one to load its snapshot into the dashboard.`}
    >
        <div className="upm-modal-scroll upm-column">
          {runs.length === 0 ? (
            <div
              className="text-center py-10"
              style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}
            >
              No runs yet for this brand.
            </div>
          ) : (
            runs.map((run) => {
              const isActive = activeRunId === run.id;
              const failed = (run.status || "").toLowerCase() === "error";
              const c = counts[run.id];
              const score =
                run.score != null && Number.isFinite(run.score)
                  ? Math.round(run.score)
                  : null;

              return (
                <button
                  key={run.id}
                  onClick={() => { onSelect(run.id); onClose(); }}
                  className="w-full text-left rounded-xl p-4 transition-all"
                  style={{
                    border: `1px solid ${isActive ? "var(--signal-blue-25)" : "var(--border)"}`,
                    backgroundColor: isActive ? "var(--signal-blue-10)" : "var(--card)",
                    cursor: "pointer",
                  }}
                >
                  {/* Row 1 — score + datetime + active marker */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="flex-shrink-0 flex flex-col items-center justify-center rounded-lg"
                        style={{
                          width: 52, height: 52,
                          backgroundColor: "var(--alpha-teal-10)",
                          border: "1px solid var(--alpha-teal-20)",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "var(--text-lg)",
                            fontWeight: 800,
                            color: "var(--alpha-teal)",
                            lineHeight: 1,
                          }}
                        >
                          {score ?? NOT_MEASURED}
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            color: "var(--muted-foreground)",
                            marginTop: 2,
                            letterSpacing: "0.04em",
                          }}
                        >
                          SCORE
                        </span>
                      </div>

                      <div className="min-w-0">
                        <div
                          className="flex items-center gap-1.5"
                          style={{
                            fontSize: "var(--text-sm)",
                            fontWeight: 700,
                            color: "var(--foreground)",
                          }}
                        >
                          <Clock className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                          {formatWhen(run.created_at)}
                        </div>
                        <div
                          className="flex items-center gap-1.5 mt-1 flex-wrap"
                          style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}
                        >
                          <Cpu className="w-3.5 h-3.5 flex-shrink-0" />
                          {(run.selected_models || []).length
                            ? run.selected_models.map((m) => (
                                <span
                                  key={m}
                                  style={{
                                    padding: "1px 7px",
                                    borderRadius: 999,
                                    border: "1px solid var(--border)",
                                    backgroundColor: "var(--muted)",
                                  }}
                                >
                                  {m}
                                </span>
                              ))
                            : <span>no models recorded</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {isActive && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--signal-blue)",
                            backgroundColor: "var(--signal-blue-10)",
                            border: "1px solid var(--signal-blue-25)",
                            padding: "2px 8px",
                            borderRadius: 999,
                          }}
                        >
                          LOADED
                        </span>
                      )}
                      {failed && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--infrared-coral)",
                          }}
                        >
                          FAILED
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Row 2 — recommendations to implement + implemented */}
                  <div
                    className="mt-3 pt-3 flex items-center gap-x-5 gap-y-2 flex-wrap"
                    style={{ borderTop: "1px solid var(--border)" }}
                  >
                    <div className="flex items-center gap-2">
                      <ListChecks className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", fontWeight: 600 }}>
                        To implement
                      </span>
                      <PriorityChip
                        label="High" value={run.rec_high}
                        color="var(--infrared-coral)" bg="rgba(220,38,38,0.10)"
                      />
                      <PriorityChip
                        label="Med" value={run.rec_medium}
                        color="var(--chart-3)" bg="rgba(245,158,11,0.12)"
                      />
                      <PriorityChip
                        label="Low" value={run.rec_low}
                        color="var(--signal-blue)" bg="var(--signal-blue-10)"
                      />
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                        ({run.rec_total} total)
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--chart-3)" }} />
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", fontWeight: 600 }}>
                        Implemented
                      </span>
                      <span
                        style={{
                          fontSize: "var(--text-sm)",
                          fontWeight: 800,
                          color: "var(--chart-3)",
                        }}
                      >
                        {loading && !c ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--muted-foreground)" }} />
                        ) : c ? (
                          c.implemented
                        ) : (
                          "—"
                        )}
                      </span>
                      {c && c.inLog > 0 && (
                        <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                          / {c.inLog} in Action Log
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
    </Modal>
  );
}